import { Paper, Typography, Grid, Box } from '@mui/material';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import { AccessTime, AttachMoney, Cancel, CheckCircle, Pending, ShoppingCart, People } from '@mui/icons-material';

type props = {
  ordersCount: number;
  completedCount: number;
  canceledCount: number;
  inProgressCount: number;
  avgPrepMinutes: number;
  grossSales: number;
  netSales: number;
  avgOrderValue: number;
  avgItemsPerOrder: number;
  uniqueClients: number;
};

export const InformationCards: React.FC<props> = ({
  ordersCount,
  completedCount,
  canceledCount,
  inProgressCount,
  avgPrepMinutes,
  grossSales,
  netSales,
  avgOrderValue,
  avgItemsPerOrder,
  uniqueClients
}) => {
  const formatCurrency = (amount: number) => 
    amount != null ? `$${amount?.toFixed(2)}` : 'Sin datos';
  const formatMinutes = (minutes: number) => 
    minutes != null ? `${minutes?.toFixed(1)} min` : 'Sin datos';
  const formatNumber = (num: number) => 
    num != null ? num?.toString() : 'Sin datos';
  const formatDecimal = (num: number) => 
    num != null ? num?.toFixed(1) : 'Sin datos';

  const salesMetrics = [
    {
      icon: <AttachMoney style={{ fontSize: '24px' }} />,
      title: 'Ventas Brutas',
      value: formatCurrency(grossSales),
      color: '#1976d2'
    },
    {
      icon: <AttachMoney style={{ fontSize: '24px' }} />,
      title: 'Ventas Netas',
      value: formatCurrency(netSales),
      color: '#1565c0'
    },
    {
      icon: <ShoppingCart style={{ fontSize: '24px' }} />,
      title: 'Valor Promedio por Orden',
      value: formatCurrency(avgOrderValue),
      color: '#0d47a1'
    }
  ];

  const orderMetrics = [
    {
      icon: <FastfoodIcon style={{ fontSize: '24px' }} />,
      title: 'Órdenes Totales',
      value: formatNumber(ordersCount),
      color: '#2e7d32'
    },
    {
      icon: <CheckCircle style={{ fontSize: '24px' }} />,
      title: 'Completadas',
      value: formatNumber(completedCount),
      color: '#388e3c'
    },
    {
      icon: <Pending style={{ fontSize: '24px' }} />,
      title: 'En Progreso',
      value: formatNumber(inProgressCount),
      color: '#43a047'
    },
    {
      icon: <Cancel style={{ fontSize: '24px' }} />,
      title: 'Canceladas',
      value: formatNumber(canceledCount),
      color: '#d32f2f'
    }
  ];

  const operationalMetrics = [
    {
      icon: <AccessTime style={{ fontSize: '24px' }} />,
      title: 'Tiempo Promedio de Preparación',
      value: formatMinutes(avgPrepMinutes),
      color: '#7b1fa2'
    },
    {
      icon: <FastfoodIcon style={{ fontSize: '24px' }} />,
      title: 'Items Promedio por Orden',
      value: formatDecimal(avgItemsPerOrder),
      color: '#9c27b0'
    },
    {
      icon: <People style={{ fontSize: '24px' }} />,
      title: 'Clientes Únicos',
      value: formatNumber(uniqueClients),
      color: '#ab47bc'
    }
  ];

  const renderMetricCards = (metrics: any[], title: string) => (
    <Box sx={{ mb: 3 }}>
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 2, 
          fontWeight: 600, 
          color: '#37474f',
          fontSize: '1.1rem'
        }}
      >
        {title}
      </Typography>
      <Grid container spacing={2}>
        {metrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <Paper
              sx={{
                p: 2.5,
                borderRadius: 2,
                backgroundColor: '#ffffff',
                border: '1px solid #e0e0e0',
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.1)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    backgroundColor: `${metric.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}
                >
                  <Box sx={{ color: metric.color }}>
                    {metric.icon}
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: '#616161',
                    lineHeight: 1.3
                  }}
                >
                  {metric.title}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: '#212121',
                  ml: 7
                }}
              >
                {metric.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ mb: 3 }}>
      {renderMetricCards(salesMetrics, 'Ventas')}
      {renderMetricCards(orderMetrics, 'Órdenes')}
      {renderMetricCards(operationalMetrics, 'Operaciones')}
    </Box>
  );
};
